import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client.js';
import { PostFilters, buildPostsQuery } from '../components/PostFilters.jsx';
import { PostListRow } from '../components/PostListRow.jsx';
import { useProjectScope } from '../lib/projectScope.js';
import { UploadPostModal } from '../components/UploadPostModal.jsx';
import useAiPageContext from '../hooks/useAiPageContext.js';

/**
 * Posts index — cross-project list of recent posts with status + date
 * filters and inline Approve/Reject. The full backlog / approval-queue view.
 * Scoped to the topbar's selected project when one is active (ADR-0029).
 */
export default function Posts() {
  const [scope] = useProjectScope();
  const [filters, setFilters] = useState({ state: '', pillar: '', from: '', to: '' });
  const [limit, setLimit] = useState(50);
  const [showUpload, setShowUpload] = useState(false);

  const qs = buildPostsQuery({ ...filters, project: scope, limit });
  const q = useQuery({
    queryKey: ['posts', { ...filters, project: scope, limit }],
    queryFn: ({ signal }) => apiGet(`/posts?${qs}`, { signal }),
  });

  // Let the AI assistant answer about the posts currently on screen (read-only
  // — a compact snapshot of what the user already sees; navigation covers the
  // rest of the app).
  useAiPageContext('posts_list', () => ({
    filters,
    scope: scope || 'all projects',
    count: q.data?.posts?.length || 0,
    posts: (q.data?.posts || []).slice(0, 30).map((p) => ({
      id: p.id, title: p.title, project: p.project_name, pillar: p.pillar_key, state: p.state,
    })),
  }));

  return (
    <div className="posts-page">
      <header className="page-header page-header-row">
        <div>
          <h1>Posts</h1>
          <p className="muted">Every post across every project — AI-generated and your own uploads. Filter by status and date, and approve or reject straight from the list.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowUpload(true)}>+ Upload your own</button>
      </header>

      {showUpload && <UploadPostModal defaultProjectKey={scope || ''} onClose={() => setShowUpload(false)} />}

      <PostFilters {...filters} onChange={(f) => { setFilters(f); setLimit(50); }} />

      {q.isLoading && <div className="loading">Loading posts…</div>}
      {q.isError && <div className="error">Couldn’t load posts: {q.error.message}</div>}

      {q.data && q.data.posts.length === 0 && (
        <div className="empty">No posts match this filter.</div>
      )}

      {q.data && q.data.posts.length > 0 && (
        <>
          <div className="post-table">
            <div className="post-row post-row-head post-row-rich">
              <span>Title</span>
              <span>State</span>
              <span>When</span>
              <span>Actions</span>
            </div>
            {q.data.posts.map((p) => <PostListRow key={p.id} post={p} showProject />)}
          </div>

          {q.data.posts.length >= limit && (
            <button type="button" className="btn-compact" onClick={() => setLimit(limit + 50)}>
              Load 50 more
            </button>
          )}
        </>
      )}
    </div>
  );
}
